import { useCallback, useMemo, useState } from "react"
import type { RowGroupOpenedEvent } from "ag-grid-community"

import type { StoreWithStatus } from "../../useAllocationData"
import type { GroupBy, Row } from "./productRows"
import type { ProductListing } from "./useProductSettings"

// Group by Store lists every variant under every store: ~800k rows for a full catalogue. So a store
// builds its variant rows, listings and summaries only once it is expanded, and nothing expands
// every store at once. Everything that follows from that lives here.

export type StoreGroupStore = Pick<StoreWithStatus, "id" | "allocationLocationId" | "storeNumber">

export type StoreGroups = {
  /** Store ids whose group is expanded. */
  openStores: ReadonlySet<string>
  /** Per store, the variant ids listed there, in the order the table lists them. */
  variantIdsByStore: ReadonlyMap<string, string[]>
  /** The listings of the open stores' variants cut down to that store, by storeScopeId. */
  storeListings: ReadonlyMap<string, ProductListing>
  /** Why Expand all and the search's auto-expand are off; undefined when they are on. */
  expandAllBlocked: string | undefined
  onRowGroupOpened: (event: RowGroupOpenedEvent<Row>) => void
}

const NO_STORES: ReadonlySet<string> = new Set()

export const storeGroupKey = (storeId: string) => `store:${storeId}`
export const storeScopeId = (storeId: string, variantId: string) => `store/${storeId}/${variantId}`

/** The listing cut down to one store: the sizes listed there, at that store's location only. */
function atStore(listing: ProductListing, store: StoreGroupStore): ProductListing {
  const only = [store.allocationLocationId]
  return {
    ...listing,
    scopeId: storeScopeId(store.id, listing.variantId),
    skus: listing.skus
      .filter((sku) => sku.locationIds.includes(store.allocationLocationId))
      .map((sku) => ({ ...sku, locationIds: only })),
    storeIds: [store.id],
  }
}

/** Per store, the variant ids listed there, in table order. */
export function variantIdsByStoreOf(ordered: readonly ProductListing[]): Map<string, string[]> {
  const index = new Map<string, string[]>()
  for (const listing of ordered) {
    for (const storeId of listing.storeIds) {
      const ids = index.get(storeId)
      if (ids) ids.push(listing.variantId)
      else index.set(storeId, [listing.variantId])
    }
  }
  return index
}

/** The open stores' variants, each listing cut down to its store, by storeScopeId. */
export function openStoreListings(
  ordered: readonly ProductListing[],
  stores: readonly StoreGroupStore[],
  openStores: ReadonlySet<string>,
  variantIdsByStore: ReadonlyMap<string, string[]>,
): Map<string, ProductListing> {
  const out = new Map<string, ProductListing>()
  if (openStores.size === 0) return out
  const byId = new Map(ordered.map((listing) => [listing.variantId, listing]))
  for (const store of stores) {
    if (!openStores.has(store.id)) continue
    for (const variantId of variantIdsByStore.get(store.id) ?? []) {
      const listing = atStore(byId.get(variantId)!, store)
      out.set(listing.scopeId, listing)
    }
  }
  return out
}

export function useStoreGroups({
  groupBy,
  ordered,
  stores,
}: {
  groupBy: GroupBy
  /** The listed variants in table order. */
  ordered: readonly ProductListing[]
  stores: readonly StoreGroupStore[]
}): StoreGroups {
  const active = groupBy === "store"
  const [openStores, setOpenStores] = useState(NO_STORES)
  // Leaving store grouping closes every store, so coming back never rebuilds stale ones.
  const [openFor, setOpenFor] = useState(groupBy)
  if (openFor !== groupBy) {
    setOpenFor(groupBy)
    setOpenStores(NO_STORES)
  }

  const variantIdsByStore = useMemo(
    () => (active ? variantIdsByStoreOf(ordered) : new Map<string, string[]>()),
    [active, ordered],
  )
  const storeListings = useMemo(
    () => openStoreListings(ordered, stores, openStores, variantIdsByStore),
    [ordered, stores, openStores, variantIdsByStore],
  )

  const onRowGroupOpened = useCallback(({ data, expanded }: RowGroupOpenedEvent<Row>) => {
    const storeId = data?.kind === "group" ? data.storeId : undefined
    if (storeId === undefined) return
    setOpenStores((prev) => {
      if (prev.has(storeId) === expanded) return prev
      const next = new Set(prev)
      if (expanded) next.add(storeId)
      else next.delete(storeId)
      return next
    })
  }, [])

  return useMemo(
    () => ({
      openStores,
      variantIdsByStore,
      storeListings,
      expandAllBlocked: active
        ? "Open stores one at a time: every store at once is too many rows"
        : undefined,
      onRowGroupOpened,
    }),
    [openStores, variantIdsByStore, storeListings, active, onRowGroupOpened],
  )
}
